/**
 * Header Component
 * Reusable header matching design specifications
 * Features: WHO branding, navigation links, AI Marketplace branding, responsive layout
 * TRANSPARENT with blur effect on scroll
 */
import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  CircularProgress,
  Menu,
  MenuItem,
  Tooltip,
  ListItemIcon,
  Typography as MuiTypography,
} from '@mui/material';
import { Typography } from '@components/wrappers';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMainMenu } from '@hooks/useMenu';
import { useOpenMenuLink } from '@hooks/useMenuLink';
import { useAISearchPanel } from '@hooks/useAISearchPanel';
import { AIAssistantIcon } from '@components/chatbot/copilot/AIAssistantIcon';
import { ROUTES } from '@core/constants/routes.constants';
import { useAuthStore } from '@store/auth.store';
import { useAuth } from '@features/auth/hooks/useAuth';
import { Colors, FontFamily } from '@styles/index';
import {
  toolbarSx,
  leftSectionBoxSx,
  whoLogoSx,
  brandTitleContainerSx,
  brandTitleTypographySx,
  centerNavContainerSx,
  navPillContainerSx,
  navAndSearchRowSx,
  headerSearchButtonSx,
  headerSearchLabelSx,
  rightSectionBoxSx,
  userProfileClickableSx,
  userNameTypographySx,
  profileIconButtonSx,
  personOutlineIconSx,
  userMenuSx,
  menuPaperSx,
  userEmailMenuItemTypographySx,
  unauthenticatedAccountButtonSx,
  mobileHamburgerButtonSx,
  mobileDrawerSx,
  drawerContainerBoxSx,
  drawerHeaderBoxSx,
  drawerCloseButtonSx,
  drawerNavListSx,
  drawerLoadingBoxSx,
  drawerNavLinkButtonSx,
  externalLinkIndicatorSpanSx,
  drawerDividerSx,
} from './Header.styles';

interface HeaderProps {
  /** Optional custom background color (overrides blur transparent) */
  backgroundColor?: string;
  /** Position of the AppBar - defaults to sticky */
  position?: 'fixed' | 'absolute' | 'sticky' | 'static' | 'relative';
  /** Elevation shadow depth */
  elevation?: number;
}

export const Header: React.FC<HeaderProps> = ({
  backgroundColor,
  position = 'sticky',
  elevation = 0,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

  // Auth state and logout
  const { user, isAuthenticated } = useAuthStore();
  const { logout } = useAuth();

  // Fetch menu items from API
  const { data: menuItems = [], isLoading: isMenuLoading } = useMainMenu();
  const openMenuLink = useOpenMenuLink();
  const { showPanel: showSearchPanel } = useAISearchPanel();

  // Track scroll position to apply progressive blur effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setScrollY(scrollPosition);
    };

    // Initial check
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Calculate blur intensity based on scroll position
  // Starts at 0 (transparent) and increases to 30px (very strong blur) after 50px scroll
  const blurIntensity = Math.min((scrollY / 50) * 30, 30);
  const isScrolled = scrollY > 0;

  // Background opacity increases with scroll
  const backgroundOpacity = Math.min((scrollY / 100) * 0.95, 0.95);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  /**
   * Opens user menu dropdown
   */
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorElUser(event.currentTarget);
  };

  /**
   * Closes user menu dropdown
   */
  const handleCloseUserMenu = (): void => {
    setAnchorElUser(null);
  };

  /**
   * Handles logout action
   * Clears authentication and redirects to login
   */
  const handleLogout = async (): Promise<void> => {
    handleCloseUserMenu();
    try {
      await logout();
      navigate(ROUTES.LOGIN);
    } catch (error) {
      // Logout error - still redirect to login
      // Still redirect to login even if logout fails
      navigate(ROUTES.LOGIN);
    }
  };

  const handleLinkClick = (href: string, isExternal: boolean) => (e: React.MouseEvent) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    openMenuLink(href, isExternal);
  };

  // Filter out home link from navigation (we'll add it separately if needed)
  // Or include all items from API
  const apiNavigationLinks = menuItems.filter((item) => !item.isHome);

  // Use navigation links from API
  const navigationLinks = [...apiNavigationLinks];

  // Get home link separately
  const homeLink = menuItems.find((item) => item.isHome);

  /**
   * Get user's full name
   * Priority: first_name + last_name > name > email > 'User'
   */
  const getUserFullName = (): string => {
    if (!user) return 'User';

    // Try first_name + last_name
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`.trim();
    }

    // Fallback to name
    if (user.name) {
      return user.name;
    }

    // Fallback to email (first part before @)
    if (user.email) {
      return user.email.split('@')[0];
    }

    return 'User';
  };

  const userFullName = getUserFullName();

  return (
    <>
      <AppBar
        position={position}
        elevation={elevation}
        sx={{
          // Transparent initially, strong blur with semi-transparent background when scrolled
          backgroundColor:
            backgroundColor ||
            (isScrolled ? `rgba(255, 255, 255, ${backgroundOpacity})` : 'transparent'),
          background:
            backgroundColor ||
            (isScrolled ? `rgba(255, 255, 255, ${backgroundOpacity})` : 'transparent'),
          backdropFilter: isScrolled ? `blur(${blurIntensity}px) saturate(180%)` : 'blur(0px)',
          WebkitBackdropFilter: isScrolled
            ? `blur(${blurIntensity}px) saturate(180%)`
            : 'blur(0px)',
          boxShadow: isScrolled ? '0 2px 8px rgba(0, 0, 0, 0.08)' : 'none',
          borderBottom: isScrolled ? '1px solid rgba(0, 0, 0, 0.05)' : 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          top: 0,
          left: 0,
          right: 0,
          width: '100%',
          zIndex: 1300,
          color: Colors.navy,
          paddingY: 0,
        }}
      >
        <Toolbar sx={toolbarSx}>
          {/* Left Section - WHO Logo + brand title (same width as right section so the menu is centered) */}
          <Box sx={leftSectionBoxSx}>
            <Box
              component="img"
              src="/assets/who_logo.svg"
              alt="World Health Organization"
              onClick={() => navigate(ROUTES.HOME)}
              sx={whoLogoSx}
            />

            {/* Brand title - centered between logo and navigation (equal spacing on both sides) */}
            <Box sx={brandTitleContainerSx}>
              <MuiTypography sx={brandTitleTypographySx}>OneWHO AI Marketplace</MuiTypography>
            </Box>
          </Box>

          {/* Center Navigation - Desktop Only - Flexible center */}
          {!isMobile && (
            <Box sx={centerNavContainerSx}>
              {/* Show loading spinner while menu loads */}
              {isMenuLoading ? (
                <CircularProgress size={20} sx={{ color: Colors.navy }} />
              ) : (
                <Box sx={navAndSearchRowSx}>
                  <Box sx={navPillContainerSx}>
                    {/* Home Link (if available from API) */}
                    {homeLink && (
                      <Box
                        component="button"
                        onClick={handleLinkClick(homeLink.href, homeLink.isExternal)}
                        sx={{
                          textDecoration: 'none',
                          cursor: 'pointer',
                          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                          padding: { md: '7px 10px', lg: '8px 14px', xl: '9px 16px' },
                          borderRadius: '40px',
                          fontFamily: FontFamily.primary,
                          fontSize: { md: '12px', lg: '13px', xl: '14px' },
                          lineHeight: 1,
                          fontWeight: 600,
                          letterSpacing: '0.2px',
                          whiteSpace: 'nowrap',
                          color:
                            location.pathname === ROUTES.HOME || location.pathname === '/'
                              ? Colors.white
                              : '#374151',
                          border: 'none',
                          background:
                            location.pathname === ROUTES.HOME || location.pathname === '/'
                              ? '#667eea'
                              : 'transparent',
                          boxShadow:
                            location.pathname === ROUTES.HOME || location.pathname === '/'
                              ? '0 3px 10px rgba(102, 126, 234, 0.3)'
                              : 'none',
                          '&:hover': {
                            color:
                              location.pathname === ROUTES.HOME || location.pathname === '/'
                                ? Colors.white
                                : '#667eea',
                            background:
                              location.pathname === ROUTES.HOME || location.pathname === '/'
                                ? '#5a6fd6'
                                : 'rgba(102, 126, 234, 0.08)',
                            transform: 'translateY(-1px)',
                          },
                        }}
                      >
                        {homeLink.title}
                      </Box>
                    )}

                    {/* Other Navigation Links from API */}
                    {navigationLinks.map((link) => {
                      const isActive =
                        location.pathname === link.href ||
                        (link.href.startsWith('/') &&
                          link.href !== '/' &&
                          location.pathname.startsWith(link.href));

                      return (
                        <Box
                          key={link.key}
                          component="button"
                          onClick={handleLinkClick(link.href, link.isExternal)}
                          sx={{
                            textDecoration: 'none',
                            cursor: 'pointer',
                            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                            padding: { md: '7px 10px', lg: '8px 14px', xl: '9px 16px' },
                            borderRadius: '40px',
                            fontFamily: FontFamily.primary,
                            fontSize: { md: '12px', lg: '13px', xl: '14px' },
                            lineHeight: 1,
                            fontWeight: 600,
                            letterSpacing: '0.2px',
                            whiteSpace: 'nowrap',
                            color: isActive ? Colors.white : '#374151',
                            border: 'none',
                            background: isActive ? '#667eea' : 'transparent',
                            boxShadow: isActive ? '0 3px 10px rgba(102, 126, 234, 0.3)' : 'none',
                            '&:hover': {
                              color: isActive ? Colors.white : '#667eea',
                              background: isActive ? '#5a6fd6' : 'rgba(102, 126, 234, 0.08)',
                              transform: 'translateY(-1px)',
                            },
                          }}
                        >
                          {link.title}
                        </Box>
                      );
                    })}
                  </Box>

                  {/* AI Search - same height as the menu bar (row stretches its children) */}
                  {isAuthenticated && (
                    <Box
                      component="button"
                      type="button"
                      onClick={showSearchPanel}
                      aria-label="Open AI Search"
                      sx={headerSearchButtonSx}
                    >
                      <AIAssistantIcon size={18} />
                      <Box component="span" sx={headerSearchLabelSx}>
                        Search
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Right Section - User Icon & Mobile Menu */}
          <Box
            display="flex"
            alignItems="center"
            gap={{ xs: 0.5, sm: 0.75, md: 1, lg: 1.5 }}
            sx={rightSectionBoxSx}
          >
            {/* User Profile Menu - Only show if authenticated */}
            {isAuthenticated && user && (
              <Box sx={{ flexGrow: 0 }}>
                <Box sx={userProfileClickableSx} onClick={handleOpenUserMenu}>
                  {/* User Full Name */}
                  <Typography variant="body2" sx={userNameTypographySx}>
                    {userFullName}
                  </Typography>

                  {/* Profile Icon */}
                  <Tooltip title="User menu">
                    <IconButton onClick={handleOpenUserMenu} sx={profileIconButtonSx}>
                      <PersonOutlineIcon sx={personOutlineIconSx} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Menu
                  sx={userMenuSx}
                  id="user-menu"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                  disableScrollLock={true}
                  slotProps={{
                    paper: {
                      sx: menuPaperSx,
                    },
                  }}
                >
                  {/* User Email (disabled item) */}
                  <MenuItem disabled>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={userEmailMenuItemTypographySx}
                    >
                      {user.email}
                    </Typography>
                  </MenuItem>
                  <Divider />

                  {/* Logout Option */}
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    Logout
                  </MenuItem>
                </Menu>
              </Box>
            )}

            {/* Show Account Icon if not authenticated (for visual consistency) */}
            {!isAuthenticated && (
              <IconButton
                color="inherit"
                aria-label="User account"
                size={isMobile ? 'small' : 'medium'}
                onClick={() => navigate(ROUTES.LOGIN)}
                sx={unauthenticatedAccountButtonSx}
              >
                <AccountCircleIcon />
              </IconButton>
            )}

            {/* Hamburger Menu - Mobile Only */}
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                onClick={toggleMobileMenu}
                size="small"
                sx={mobileHamburgerButtonSx}
              >
                {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer Menu with Blur Effect */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={toggleMobileMenu}
        disableScrollLock={true}
        sx={mobileDrawerSx}
      >
        <Box sx={drawerContainerBoxSx}>
          {/* Drawer Header */}
          <Box sx={drawerHeaderBoxSx}>
            <Typography variant="h6">Menu</Typography>
            <IconButton onClick={toggleMobileMenu} size="small" sx={drawerCloseButtonSx}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Navigation Links */}
          <List sx={drawerNavListSx}>
            {isMenuLoading ? (
              <ListItem>
                <Box sx={drawerLoadingBoxSx}>
                  <CircularProgress size={24} />
                </Box>
              </ListItem>
            ) : (
              <>
                {/* Home Link (if available from API) */}
                {homeLink && (
                  <React.Fragment>
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={handleLinkClick(homeLink.href, homeLink.isExternal)}
                        sx={drawerNavLinkButtonSx}
                      >
                        <ListItemText
                          primary={homeLink.title}
                          primaryTypographyProps={{
                            variant: 'body1',
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                    {navigationLinks.length > 0 && <Divider sx={drawerDividerSx} />}
                  </React.Fragment>
                )}

                {/* Other Navigation Links from API */}
                {navigationLinks.map((link, index) => (
                  <React.Fragment key={link.key}>
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={handleLinkClick(link.href, link.isExternal)}
                        sx={drawerNavLinkButtonSx}
                      >
                        <ListItemText
                          primary={link.title}
                          primaryTypographyProps={{
                            variant: 'body1',
                          }}
                        />
                        {link.isExternal && (
                          <Box component="span" sx={externalLinkIndicatorSpanSx}>
                            ↗
                          </Box>
                        )}
                      </ListItemButton>
                    </ListItem>
                    {index < navigationLinks.length - 1 && <Divider sx={drawerDividerSx} />}
                  </React.Fragment>
                ))}
              </>
            )}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default Header;
